// Debug showcase: lines up game models under in-game lighting for before/after comparisons.
// Usage (browser console): __app.showcase('characters' | 'props' | 'stations' | 'ingredients')
import { Scene, Camera, Node, Light } from '../engine/renderer.js';
import { GeoBuilder, m } from '../engine/geometry.js';
import { col } from '../engine/math.js';
import * as M from '../game/models.js';
import { LEVELS, SPECIES, CHEF_COLORS, INGREDIENTS, RECIPES } from '../game/data.js';
import { Vessel, Cup, Extinguisher, Ingredient } from '../game/items.js';

export function buildShowcase(kind) {
  const th = LEVELS[0].theme;
  const s = new Scene();
  const e = s.env;
  e.sunDir = [-0.45, -1, -0.55];
  e.sunColor = col('#ffe2c4');
  e.sunIntensity = 1.9;
  e.skyAmb = col(th.neb1).map((v) => v * 0.6 + 0.08);
  e.groundAmb = col(th.floor).map((v) => v * 0.5 + 0.02);
  e.rim = col(th.accent2);
  e.rimIntensity = 0.35;
  e.exposure = 1.05;
  e.bloomThreshold = 1.1;
  e.shadowRadius = 5;
  s.sky.neb1 = col(th.neb1); s.sky.neb2 = col(th.neb2); s.sky.neb3 = col(th.neb3);
  s.sky.planetCol = col(th.planet); s.sky.planetCol2 = col(th.planet2); s.sky.seed = th.seed;
  // floor + back counter
  const b = new GeoBuilder();
  for (let x = -6; x < 6; x++)
    for (let z = -3; z < 3; z++) b.roundBox(0.965, 0.06, 0.965, 0.02, m((x + z) & 1 ? '#54468c' : '#5f4f9e', { rough: 0.25, refl: 0.5 }), { p: [x + 0.5, 0, z + 0.5] }, 1);
  b.box(12, 0.04, 6, m(th.accent2, { emit: 0.9 }), { p: [0, -0.02, 0] });
  s.add(new Node(M.meshFrom(b)));
  const counters = new GeoBuilder();
  const place = [];
  const add = (node, x, z = 0, onCounter = false) => {
    if (onCounter) {
      counters.roundBox(0.98, 0.84, 0.98, 0.04, m('#3b3060', { rough: 0.4, metal: 0.2 }), { p: [x, 0.42, z] });
      counters.roundBox(1.0, 0.07, 1.0, 0.025, m('#b6aec0', { rough: 0.2 }), { p: [x, 0.865, z] }, 2);
    }
    node.position = [x, onCounter ? 0.9 : 0, z];
    s.add(node);
    place.push(node);
    return node;
  };
  const cam = new Camera();
  cam.fov = (30 * Math.PI) / 180;
  const theme = th;
  if (kind === 'characters') {
    CHEF_COLORS.forEach((c, i) => {
      const p = M.buildChef(c);
      p.root.rotation[1] = 0.25 - i * 0.12;
      p.root.scale = [1.12, 1.12, 1.12];
      add(p.root, -4.2 + i * 1.05, 0.4);
    });
    SPECIES.forEach((sp, i) => {
      const p = M.buildAlien(sp.id);
      add(p.root, 0.3 + i * 1.0, -0.2);
      p.root.rotation[1] = -0.15;
    });
    cam.position = [0, 2.6, 10.5];
    cam.target = [0, 0.6, 0];
  } else if (kind === 'props') {
    const k = new Vessel('kettle');
    add(k.node, -3, 0, true);
    const j = new Vessel('blender');
    add(j.node, -2, 0, true);
    const c1 = new Cup();
    add(c1.node, -1, 0, true);
    const c2 = new Cup();
    c2.fill('spiced_tea');
    add(c2.node, 0, 0, true);
    const c3 = new Cup();
    c3.setDirty(true);
    add(c3.node, 1, 0, true);
    const ex = new Extinguisher();
    add(ex.node, 2, 0, true);
    cam.position = [-0.5, 2.5, 6.4];
    cam.target = [-0.5, 1.0, 0];
  } else if (kind === 'stations') {
    const types = ['stove', 'blender', 'chop', 'grind', 'sink', 'rack', 'bin', 'carbonator', 'crate', 'extinguisherHook'];
    types.forEach((t, i) => {
      const n = new Node(M.stationMesh(t, theme));
      add(n, -4.5 + i, 0, true);
      if (t === 'stove') n.add(new Node(M.stoveRingMesh()));
      if (t === 'grind') n.add(new Node(M.pestleMesh()));
    });
    cam.position = [0, 3.6, 10.8];
    cam.target = [0, 0.8, 0];
  } else if (kind === 'held') {
    const I = [new Ingredient('banana'), new Ingredient('banana'), new Ingredient('mango'), new Ingredient('pineapple'), null];
    I[1].prepare();
    const cup = new Cup();
    cup.fill('smoothie_star');
    I[4] = cup;
    I.forEach((it, i) => {
      const p = M.buildChef(CHEF_COLORS[i % CHEF_COLORS.length]);
      p.armL.rotation = [-1.2, 0, 0.2];
      p.armR.rotation = [-1.2, 0, -0.2];
      p.hand.add(it.node);
      p.root.rotation[1] = 0.6;
      add(p.root, -2.4 + i * 1.2, 0.3);
    });
    cam.position = [0, 2.2, 5.2];
    cam.target = [0, 0.6, 0];
  } else if (kind === 'drinks') {
    const ids = (window.__drinkIds || Object.keys(RECIPES)).slice(0, 48);
    ids.forEach((id, i) => {
      const c = new Cup();
      c.fill(id);
      c.node.scale = [1.6, 1.6, 1.6];
      add(c.node, -4.2 + (i % 8) * 1.2, -2.2 + Math.floor(i / 8) * 0.85);
    });
    cam.position = [0, 7.5, 5.5];
    cam.target = [0, 0, 0];
  } else if (kind === 'ingredients') {
    Object.keys(INGREDIENTS).forEach((id, i) => {
      const n = new Node(M.ingredientMesh(id, 'raw'));
      n.scale = [1.6, 1.6, 1.6];
      add(n, -4.5 + (i % 10) * 1.0, (i < 10 ? -0.6 : 0.6), false);
    });
    cam.position = [0, 3.2, 5.2];
    cam.target = [0, 0.1, 0];
  }
  s.add(new Node(M.meshFrom(counters)));
  // lights like the kitchen
  s.lights.push(new Light({ position: [-3, 3.2, 1.5], color: '#fff0e0', intensity: 9, range: 8 }));
  s.lights.push(new Light({ position: [3, 3.2, 1.5], color: th.accent2, intensity: 7, range: 8 }));
  s.lights.push(new Light({ position: [0, 3, -1.5], color: '#ffd6a0', intensity: 8, range: 7 }));
  return { scene: s, camera: cam, update() {} };
}
